import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { urlValidator } from "../validators.js";
import { PdBaseUIInput } from "../base/pd-base-ui-input.js";
import { css, html } from "lit";
import { property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-input-url/PdInputUrl.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* URL input component with protocol prefix display.
*
* Renders a text input with a fixed protocol prefix (`https://` by default).
* When the user pastes or types `http://`, the prefix switches and turns red
* as a visual warning for insecure URLs.
*
* @tagname pd-input-url
* @summary URL input with protocol prefix and insecure-warning.
*
* @event pd-form-element-change - Fired when URL value changes. Detail: `{ value, name, parsedValue, state }`.
*/
var PdInputUrl = class extends PdBaseUIInput {
	constructor() {
		super();
		this.placeholder = "example.com";
		this.allowInsecure = true;
		this._protocol = "https://";
		this._validators = [urlValidator];
	}
	static {
		this.styles = [PdBaseUIInput.styles, css`
      :host {
        display: block;
      }

      .url-container {
        display: flex;
        flex-direction: column;
        width: var(--pd-input-field-width, 250px);
        max-width: 100%;
      }

      .url-field {
        display: flex;
        align-items: center;
        height: var(--pd-input-field-height, 2.5rem);
        background: var(
          --pd-input-field-bg-col,
          var(--pd-default-bg-col, #fff)
        );
        border: var(
          --pd-input-field-border,
          1px solid var(--pd-default-light-col, #cbd5e1)
        );
        border-bottom: var(
          --pd-input-field-border-bottom,
          2px solid var(--pd-default-col, #2563eb)
        );
        border-radius: var(--pd-radius-md, 6px);
        box-sizing: border-box;
        overflow: hidden;
      }

      .url-field:focus-within {
        border: var(
          --pd-input-field-border-focus,
          2px solid var(--pd-default-col, #2563eb)
        );
      }

      .url-field.disabled {
        background: var(--pd-input-field-bg-col-disabled, #e9e9e9);
        border-bottom: var(
          --pd-input-field-border-bottom-disabled,
          2px solid var(--pd-default-disabled-col, #94a3b8)
        );
        cursor: not-allowed;
      }

      .url-prefix {
        padding-left: 0.5rem;
        font-family: var(--pd-default-font-input-family, monospace);
        font-size: var(--pd-default-font-input-size, 0.95rem);
        color: var(--pd-default-font-muted-col, #94a3b8);
        user-select: none;
        line-height: 1;
        white-space: nowrap;
      }

      .url-prefix.insecure {
        color: var(
          --pd-input-url-insecure-col,
          var(--pd-default-error-col, #ef4444)
        );
      }

      .url-input {
        flex: 1;
        height: 100%;
        padding: var(--pd-input-field-padding, 0.25rem) 0.5rem
          var(--pd-input-field-padding, 0.25rem) 0.25rem;
        font-family: var(--pd-default-font-input-family, monospace);
        font-size: var(--pd-default-font-input-size, 0.95rem);
        color: var(--pd-default-font-input-col, #1e293b);
        background: transparent;
        border: none;
        box-sizing: border-box;
        outline: none;
        min-width: 0;
      }

      .url-input:disabled {
        cursor: not-allowed;
      }

      .url-container.error .url-field {
        border: 1px solid var(--pd-default-error-col, #ef4444);
        background-color: var(--pd-default-error-light-col);
      }

      .url-container.focus.error .url-field {
        border: 2px solid var(--pd-default-error-col, #ef4444);
      }

      .url-container.valid .url-field {
        border: 1px solid var(--pd-default-success-col, #22c55e);
        background-color: var(--pd-default-success-light-col);
      }
    `];
	}
	render() {
		const inputId = `${this.id}UrlInput`;
		const errorId = `${this.id}Error`;
		const displayValue = this._getDisplayValue();
		return html`
      ${this._renderLabel(inputId)}
      <div class="${classMap(this.getClassmap({ "url-container": true }))}">
        <div class="url-field ${this.disabled ? "disabled" : ""}">
          <span
            class="url-prefix ${this._protocol === "http://" ? "insecure" : ""}"
            >${this._protocol}</span
          >
          <input
            id="${inputId}"
            type="text"
            class="url-input"
            .value="${displayValue}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            ?readonly="${this.readonly}"
            @input="${this._onInput}"
            @blur="${this._onBlur}"
            @focus="${this._onFocus}"
            @paste="${this._onPaste}"
            aria-describedby="${errorId}"
          />
        </div>
      </div>
      ${this._renderErrorMsg(errorId)}
    `;
	}
	/** Strips protocol from _value for display. */
	_getDisplayValue() {
		if (!this._value) return "";
		return this._value.replace(/^https?:\/\//, "");
	}
	_onInput(event) {
		const raw = event.target.value.trim();
		if (raw === "") {
			this._protocol = "https://";
			this._handleChangedValue("", event, true);
			return;
		}
		const parsed = this._extractProtocolAndHost(raw);
		this._protocol = parsed.protocol;
		this._handleChangedValue(this._protocol + parsed.host, event, true);
	}
	_onPaste(event) {
		const pasted = event.clipboardData?.getData("text")?.trim();
		if (!pasted) return;
		if (/^https?:\/\//i.test(pasted)) {
			event.preventDefault();
			const parsed = this._extractProtocolAndHost(pasted);
			this._protocol = parsed.protocol;
			this._handleChangedValue(this._protocol + parsed.host, event, true);
		}
	}
	_onBlur() {
		this._touched = true;
		this._focused = false;
		this.runValidators();
	}
	_onFocus() {
		this._focused = true;
	}
	/**
	* Extracts protocol and host/path from a raw input string.
	* If `allowInsecure` is false, always returns `https://`.
	*/
	_extractProtocolAndHost(raw) {
		const httpMatch = raw.match(/^http:\/\//i);
		const httpsMatch = raw.match(/^https:\/\//i);
		let protocol = "https://";
		let host = raw;
		if (httpsMatch) {
			protocol = "https://";
			host = raw.slice(8);
		} else if (httpMatch) {
			protocol = this.allowInsecure ? "http://" : "https://";
			host = raw.slice(7);
		}
		return {
			protocol,
			host
		};
	}
};
__decorate([property({ type: String })], PdInputUrl.prototype, "placeholder", void 0);
__decorate([property({ type: Boolean })], PdInputUrl.prototype, "allowInsecure", void 0);
__decorate([state()], PdInputUrl.prototype, "_protocol", void 0);
__decorate([query("input[type=\"text\"]")], PdInputUrl.prototype, "_textInput", void 0);
//#endregion
export { PdInputUrl };
